import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Text
} from "@prismicio/editor-ui"
import { useState } from "react"

import type { OnboardingStep, OnboardingTracking } from "../../service"
import { OnboardingStepDialog } from "./OnboardingStepDialog"

interface OnboardingProgressStepperProps {
  steps: OnboardingStep[]
  completedSteps: string[]
  onToggleStep: (step: OnboardingStep) => Promise<void>
  tracking: OnboardingTracking
}

export function OnboardingProgressStepper(
  props: OnboardingProgressStepperProps
) {
  const { steps, completedSteps, onToggleStep, tracking } = props
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(steps[0])

  if (!activeStep) {
    return null
  }

  function getStepIndex(stepId: string) {
    return steps.findIndex(({ id }) => id === stepId)
  }

  function isStepComplete(step: OnboardingStep) {
    return completedSteps.includes(step.id)
  }

  function showStep(step: OnboardingStep) {
    setActiveStep(step)
    setDialogOpen(true)

    void tracking.track({
      event: "shared-onboarding:step-opened",
      stepId: step.id,
      stepTitle: step.title,
      source: tracking.source
    })
  }

  async function toggle() {
    if (activeStep) await onToggleStep(activeStep)
    setDialogOpen(false)
  }

  return (
    <>
      <OnboardingStepDialog
        isOpen={isDialogOpen}
        step={activeStep}
        stepIndex={getStepIndex(activeStep.id) + 1}
        isStepComplete={isStepComplete(activeStep)}
        onClose={() => setDialogOpen(false)}
        onActionClick={toggle}
      />
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            size="large"
            color="grey"
            sx={{ width: "100%" }}
            renderStartIcon={StartIcon}
          >
            {completedSteps.length > 0 ? "Continue" : "Start now"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" minWidth={256} collisionPadding={8}>
          <DropdownMenuLabel>
            <Text variant="small" color="grey11" sx={{ marginInline: 8 }}>
              Progress
            </Text>
          </DropdownMenuLabel>
          {steps.map((step, index) => {
            const isCompleted = isStepComplete(step)

            return (
              <DropdownMenuItem
                key={step.id}
                onSelect={() => showStep(step)}
                description={step.description}
                completed={isCompleted}
                endAdornment={
                  <Icon name="chevronRight" size="small" color="grey11" />
                }
              >
                {`${index + 1} ${step.title}`}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function StartIcon() {
  return <Icon name="arrowForward" size="small" color="grey11" />
}
